import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import { sanitizeRichText } from '@/lib/sanitizeHtml';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  MapPin, Calendar, Users, ExternalLink, ArrowLeft,
  ChevronLeft, ChevronRight, Globe, Loader2, X
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { trackWorkshopEvent } from '@/utils/workshopAnalytics';

interface WorkshopData {
  id: string;
  slug: string;
  title: string;
  description_html: string | null;
  location_name: string;
  latitude: number;
  longitude: number;
  price_display: string | null;
  price_cents: number | null;
  max_participants: number | null;
  workshop_type: string;
  external_url: string | null;
  workshop_dates: Array<{ id: string; start_date: string; end_date: string; notes: string | null; sort_order: number }>;
  workshop_photos: Array<{ id: string; photo_url: string; alt_text: string | null; sort_order: number }>;
  workshop_leaders: {
    business_name: string;
    bio: string | null;
    profile_photo_url: string | null;
    website_url: string | null;
  } | null;
}

const TYPE_LABELS: Record<string, string> = {
  astrophotography: 'Astrophotography',
  deep_sky: 'Deep Sky',
  landscape: 'Landscape',
  nature: 'Nature',
  mixed: 'Mixed',
};

export default function WorkshopDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [workshop, setWorkshop] = useState<WorkshopData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    if (slug) loadWorkshop();
  }, [slug]);

  // Track detail view once workshop is loaded
  useEffect(() => {
    if (workshop) {
      trackWorkshopEvent('detail_view', {
        workshopId: workshop.id,
        leaderId: undefined,
        source: 'direct',
      });
    }
  }, [workshop?.id]);

  const loadWorkshop = async () => {
    try {
      const { data, error } = await supabase
        .from('workshop_listings')
        .select(`
          id, slug, title, description_html, location_name, latitude, longitude,
          price_display, price_cents, max_participants, workshop_type, external_url,
          workshop_dates(id, start_date, end_date, notes, sort_order),
          workshop_photos(id, photo_url, alt_text, sort_order),
          workshop_leaders(business_name, bio, profile_photo_url, website_url)
        `)
        .eq('slug', slug)
        .eq('is_active', true)
        .single();

      if (error) throw error;
      setWorkshop(data);
    } catch (err) {
      console.error('Error loading workshop:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!workshop) {
    return (
      <>
        <SEOHead title="Workshop Not Found | Milky Way Planner" noindex />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center space-y-4">
            <h1 className="font-serif text-2xl font-bold text-foreground">Workshop not found</h1>
            <p className="text-muted-foreground">This workshop may have been removed or the link is incorrect.</p>
            <Link to="/workshops">
              <Button variant="outline" className="gap-2 rounded-[4px]">
                <ArrowLeft className="w-4 h-4" /> Back to Workshops
              </Button>
            </Link>
          </div>
        </div>
      </>
    );
  }

  const photos = (workshop.workshop_photos || []).sort((a, b) => a.sort_order - b.sort_order);
  const dates = (workshop.workshop_dates || []).sort((a, b) => a.sort_order - b.sort_order);
  const leader = workshop.workshop_leaders;
  const heroPhoto = photos[0];

  const seoTitle = `${workshop.title} | Milky Way Planner Workshops`;
  const seoDescription = `${workshop.title} in ${workshop.location_name}. ${workshop.price_display ? `${workshop.price_display}.` : ''} ${dates.length > 0 ? `Next session: ${format(parseISO(dates[0].start_date), 'MMM d, yyyy')}.` : ''}`;

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        ogImage={heroPhoto?.photo_url}
      />
      <div className="min-h-screen bg-background">
        {/* Back link */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6">
          <Link to="/workshops" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Workshops
          </Link>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Photo Gallery */}
              {photos.length > 0 && (
                <div className="space-y-3">
                  <div
                    className="relative aspect-[3/2] rounded-[4px] overflow-hidden bg-[#0f1923] cursor-zoom-in"
                    onClick={() => setLightboxOpen(true)}
                  >
                    <img
                      src={photos[activePhoto].photo_url}
                      alt={photos[activePhoto].alt_text || workshop.title}
                      className="w-full h-full object-contain"
                    />
                    {photos.length > 1 && (
                      <>
                        <button
                          onClick={() => setActivePhoto((prev) => (prev - 1 + photos.length) % photos.length)}
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setActivePhoto((prev) => (prev + 1) % photos.length)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                          {photos.map((_, i) => (
                            <button
                              key={i}
                              onClick={() => setActivePhoto(i)}
                              className={`w-2 h-2 rounded-full transition-colors ${i === activePhoto ? 'bg-white' : 'bg-white/40'}`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  {photos.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto">
                      {photos.map((photo, i) => (
                        <button
                          key={photo.id}
                          onClick={() => setActivePhoto(i)}
                          className={`flex-shrink-0 w-20 h-14 rounded-[4px] overflow-hidden border-2 transition-colors ${i === activePhoto ? 'border-[#c9922a]' : 'border-transparent opacity-60 hover:opacity-100'}`}
                        >
                          <img src={photo.photo_url} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Title + Type */}
              <div>
                <Badge variant="outline" className="mb-3 text-xs">
                  {TYPE_LABELS[workshop.workshop_type] || workshop.workshop_type}
                </Badge>
                <h1 className="font-serif text-3xl sm:text-4xl font-bold text-foreground tracking-tight mb-2">
                  {workshop.title}
                </h1>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  <span>{workshop.location_name}</span>
                </div>
              </div>

              {/* Description */}
              {workshop.description_html && (
                <div>
                  <h2 className="font-semibold text-foreground mb-3">About This Workshop</h2>
                  <div
                    className="prose prose-sm max-w-none text-muted-foreground [&_h2]:text-foreground [&_h3]:text-foreground [&_strong]:text-foreground [&_a]:text-[#966b1c]"
                    dangerouslySetInnerHTML={{ __html: sanitizeRichText(workshop.description_html) }}
                  />
                </div>
              )}

              {/* Leader Bio */}
              {leader && (
                <div className="border-t border-border pt-8">
                  <h2 className="font-semibold text-foreground mb-4">About the Instructor</h2>
                  <div className="flex items-start gap-4">
                    {leader.profile_photo_url ? (
                      <img
                        src={leader.profile_photo_url}
                        alt={leader.business_name}
                        className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-muted flex-shrink-0" />
                    )}
                    <div>
                      <h3 className="font-semibold text-foreground">{leader.business_name}</h3>
                      {leader.bio && (
                        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{leader.bio}</p>
                      )}
                      {leader.website_url && (
                        <a
                          href={leader.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm text-[#966b1c] hover:underline mt-2"
                          onClick={() => trackWorkshopEvent('website_click', {
                            workshopId: workshop.id,
                            source: 'detail_page',
                            metadata: { url: leader.website_url },
                            allowDuplicate: true,
                          })}
                        >
                          <Globe className="w-3.5 h-3.5" />
                          {leader.website_url.replace(/^https?:\/\//, '')}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Booking Card */}
              <Card className="rounded-[4px] sticky top-6">
                <div className="h-1 bg-gradient-to-r from-[#c9922a] to-[#e8b84b] rounded-t-[4px]" />
                <CardContent className="pt-6 space-y-5">
                  {/* Price */}
                  {workshop.price_display && (
                    <div>
                      <span className="font-serif text-3xl font-bold text-foreground">{workshop.price_display}</span>
                      <span className="text-sm text-muted-foreground ml-1">per person</span>
                    </div>
                  )}

                  {/* Sessions */}
                  {dates.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                        Choose a Session
                      </h3>
                      {dates.map((d) => (
                        <div key={d.id} className="p-3 rounded-[4px] border border-border bg-muted/30 space-y-1.5">
                          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                            <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            {format(parseISO(d.start_date), 'MMM d')} – {format(parseISO(d.end_date), 'MMM d, yyyy')}
                          </div>
                          {d.notes && (
                            <p className="text-xs text-muted-foreground pl-6">{d.notes}</p>
                          )}
                          <div className="flex items-center gap-3 pl-6 text-xs text-muted-foreground">
                            {workshop.price_display && <span>{workshop.price_display}</span>}
                            {workshop.max_participants && (
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {workshop.max_participants} spots
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Location */}
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Location</h3>
                    <div className="flex items-center gap-2 text-sm text-foreground">
                      <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      {workshop.location_name}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {workshop.latitude.toFixed(4)}°, {workshop.longitude.toFixed(4)}°
                    </p>
                  </div>

                  {/* CTA */}
                  {workshop.external_url && (
                    <a
                      href={workshop.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                      onClick={() => trackWorkshopEvent('booking_click', {
                        workshopId: workshop.id,
                        source: 'detail_page',
                        metadata: { url: workshop.external_url },
                        allowDuplicate: true,
                      })}
                    >
                      <Button variant="premium" size="lg" className="w-full gap-2">
                        Visit Website <ExternalLink className="w-4 h-4" />
                      </Button>
                    </a>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && photos.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors z-10"
          >
            <X className="w-6 h-6" />
          </button>

          {photos.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setActivePhoto((prev) => (prev - 1 + photos.length) % photos.length); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors z-10"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setActivePhoto((prev) => (prev + 1) % photos.length); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors z-10"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          <img
            src={photos[activePhoto].photo_url}
            alt={photos[activePhoto].alt_text || workshop.title}
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {photos.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
              {photos.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setActivePhoto(i); }}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${i === activePhoto ? 'bg-white' : 'bg-white/30'}`}
                />
              ))}
            </div>
          )}

          <div className="absolute bottom-6 right-6 text-white/50 text-sm">
            {activePhoto + 1} / {photos.length}
          </div>
        </div>
      )}
    </>
  );
}
